import type { UseFetchOptions } from 'nuxt/app'
import type { FetchContext, FetchResponse } from 'ofetch'
import type { ProgressRequestOptions } from '~/types/api/request.types'

export function useAPI<T>(
  url: string | (() => string),
  options?: UseFetchOptions<T>,
) {
  return useFetch(url, {
    ...options,
    $fetch: useNuxtApp().$api,
  })
}

export function useLazyAPI<T>(
  url: string | (() => string),
  options?: UseFetchOptions<T>,
) {
  return useLazyFetch(url, {
    ...options,
    $fetch: useNuxtApp().$api
  })
}

export function useApiWithProgress() {
  const { $apiOnResponseError } = useNuxtApp()
  const { apiBase } = useRuntimeConfig().public

  const authAccessCookie = useCookie('auth_access')

  const progressFetch = <T>(
    url: string,
    options: UseFetchOptions<T> & ProgressRequestOptions
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.open(options.method as string, `${apiBase}${url}`, true)

      if (authAccessCookie.value) {
        xhr.setRequestHeader('Authorization', `Bearer ${authAccessCookie.value}`)
      }
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value as string)
        })
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded / event.total) * 100)
          options.onUploadProgress({
            loaded: event.loaded,
            total: event.total,
            percent: percentCompleted
          })
        }
      })

      xhr.onload = async () => {
        const response = {
          _data: JSON.parse(xhr.response),
          url: xhr.responseURL,
          status: xhr.status,
          statusText: xhr.statusText,
          ok: xhr.status >= 200 && xhr.status < 300
        } as FetchResponse<T>

        if (response.ok) {
          resolve(JSON.parse(xhr.response));
        } else {
          const { onUploadProgress, ...apiOptions } = options
          await $apiOnResponseError({ response, options: apiOptions } as FetchContext)
          reject({ response })
        }
      }

      xhr.onerror = (error) => reject(error)
      xhr.send(options.body as FormData)
    })
  }

  return { progressFetch }
}
